import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTrackOpenIntercomChat, useTrackRequestedForgetPasswordCode } from "@air/analytics";
import { Auth } from "@air/api";
import { IDP_ACCOUNT_EXISTS } from "@air/errors";
import { FormikField } from "@air/formik-field";
import { Button } from "@air/primitive-button";
import { Input } from "@air/primitive-input";
import { useToasts } from "@air/provider-toast";
import { convertUnknownToError, reportErrorToBugsnag } from "@air/utils-error";
import { Form, Formik } from "formik";
import Router from "next/router";
import { useMount } from "__barrel_optimize__?names=useMount!=!react-use";
import * as Yup from "yup";
import { getLayout } from "~/components/Layouts/AppProviderLayout";
import { AuthLayout } from "~/components/Layouts/AuthLayout";
import { I18n } from "~/constants/I18n";
import { Routes } from "~/constants/routes";
import { FORGOT_PASSWORD_EMAIL_INPUT, FORGOT_PASSWORD_SUBMIT_BUTTON } from "~/constants/testIDs";
import { ExistingAccountMessageWithAuthLink } from "~/utils/Auth";
import { sanitizeEmail } from "~/utils/EmailUtils";
const ForgotPasswordValidationSchema = Yup.object().shape({
    email: Yup.string().email("Please enter a valid email address").required("Please enter a valid email address").default("")
}).required();
const OpenIntercomLink = (param)=>{
    let { dismissToast } = param;
    const { trackOpenIntercomChat } = useTrackOpenIntercomChat();
    return /*#__PURE__*/ _jsx("button", {
        className: "font-medium text-grey-1 underline",
        onClick: ()=>{
            trackOpenIntercomChat();
            dismissToast();
        },
        children: "Contact support →"
    });
};
export default function Forgot() {
    const { showToast } = useToasts();
    const { trackRequestedForgetPasswordCode } = useTrackRequestedForgetPasswordCode();
    useMount(()=>{
        Router.prefetch(Routes.auth.resetPassword);
    });
    return /*#__PURE__*/ _jsx(AuthLayout, {
        layout: {
            visibleTitleText: "Forgot your password?",
            visibleSubtitle: "Enter your email, and we’ll send you a recovery code.",
            nextSeoProps: {
                title: I18n.forgotPageTitle,
                description: I18n.forgotPageDescription
            }
        },
        children: /*#__PURE__*/ _jsx(Formik, {
            initialValues: ForgotPasswordValidationSchema.cast({}),
            validationSchema: ForgotPasswordValidationSchema,
            onSubmit: async (param, param1)=>{
                let { email } = param, { setSubmitting } = param1;
                const userEmail = sanitizeEmail(email);
                setSubmitting(true);
                try {
                    const result = await Auth.forgotPassword(userEmail);
                    trackRequestedForgetPasswordCode({
                        email: userEmail
                    });
                    if (!result) throw new Error("Error requesting code");
                    /**
             * Passing PII via query parameters is not a good idea. In general, a user's email address is already
             * pretty widely exposed on the web (not just Air) and we suffer from other more easily utilized
             * vulnerabilities. Knowing that, ignoring the potential for phishing attakcs, and realizing that this form
             * only works with a code sent via an email anyways, so we're gonna just ship it. YOLO.
             *
             * @see https://air-labs-team.slack.com/archives/CCS4C8ALT/p1616094274065400
             * @see https://owasp.org/www-community/vulnerabilities/Information_exposure_through_query_strings_in_url
             */ Router.push({
                        pathname: Routes.auth.resetPassword,
                        query: {
                            email: userEmail
                        }
                    });
                } catch (_error) {
                    const error = convertUnknownToError(_error);
                    if (error.message.includes(IDP_ACCOUNT_EXISTS.message)) {
                        showToast(/*#__PURE__*/ _jsx(ExistingAccountMessageWithAuthLink, {
                            rawErrorMessage: error.message,
                            messagePrefix: "No password on this account."
                        }), {
                            type: "assertive",
                            withCloseButton: true
                        });
                    }
                    switch(error.message){
                        case "InvalidParameterException":
                            showToast((param)=>{
                                let { onDismiss } = param;
                                return /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "Account unverified. ",
                                        /*#__PURE__*/ _jsx(OpenIntercomLink, {
                                            dismissToast: onDismiss
                                        })
                                    ]
                                });
                            }, {
                                type: "assertive",
                                withCloseButton: true
                            });
                            break;
                        case "UserNotFoundException":
                            showToast((param)=>{
                                let { onDismiss } = param;
                                return /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "No account found. ",
                                        /*#__PURE__*/ _jsx(OpenIntercomLink, {
                                            dismissToast: onDismiss
                                        })
                                    ]
                                });
                            }, {
                                type: "assertive",
                                withCloseButton: true
                            });
                            break;
                        case "LimitExceededException":
                        default:
                            showToast("Something has gone wrong. Please try again later.", {
                                type: "assertive",
                                withCloseButton: true
                            });
                            break;
                    }
                    reportErrorToBugsnag({
                        error,
                        context: 'Failed to request "forgot password" code'
                    });
                    setSubmitting(false);
                }
            },
            children: (param)=>{
                let { isSubmitting } = param;
                return /*#__PURE__*/ _jsxs(Form, {
                    children: [
                        /*#__PURE__*/ _jsx(FormikField, {
                            id: "email",
                            className: "mb-6",
                            component: /*#__PURE__*/ _jsx(Input, {
                                type: "email",
                                "data-testid": FORGOT_PASSWORD_EMAIL_INPUT,
                                autoComplete: "username",
                                size: "extra-large"
                            }),
                            label: "Email *",
                            name: "email"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            className: "mt-2 w-full",
                            "data-testid": FORGOT_PASSWORD_SUBMIT_BUTTON,
                            type: "submit",
                            size: "large",
                            isLoading: isSubmitting,
                            children: "Submit"
                        })
                    ]
                });
            }
        })
    });
}
Forgot.getLayout = getLayout;
